import prepareCsrfToken from "utils/prepareCsrfToken"
import { postWithBodyAndCsrf } from "services/apiService"

import { Document, Packer, Paragraph, Table, TableRow, TableCell, WidthType, PageOrientation } from "docx"

// Utility function for downloading a generic file (e.g., CSV)
export const downloadCsvFile = ({
    endpoint,
    filename,
    body,
    setIsDownloading,
    setError,
    errorText,
}: {
    endpoint: string;
    filename: string;
    body: object;
    setIsDownloading: React.Dispatch<React.SetStateAction<boolean>>;
    setError: React.Dispatch<React.SetStateAction<string>>;
    errorText: string;
}) => {
    setError("");
    setIsDownloading(true);
    prepareCsrfToken().then((csrfToken) =>
        postWithBodyAndCsrf(endpoint, csrfToken, JSON.stringify(body), true)
            .then((response) => {
                setIsDownloading(false);

                response.blob().then((blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                });
            })
            .catch(() => {
                setIsDownloading(false);
                setError(errorText);
            })
    );
};

// Utility function for downloading a Word file (HTML to Word conversion)
export const downloadWordFile = async ({
    endpoint,
    filename,
    body,
    setIsDownloading,
    setError,
    errorText,
    hasHeadlines,
}: {
    endpoint: string;
    filename: string;
    body: object;
    setIsDownloading: React.Dispatch<React.SetStateAction<boolean>>;
    setError: React.Dispatch<React.SetStateAction<string>>;
    errorText: string;
    hasHeadlines: boolean;
}) => {
    setIsDownloading(true);
    setError("");

    try {
        const csrfToken = await prepareCsrfToken();
        const exportData = await postWithBodyAndCsrf(endpoint, csrfToken, JSON.stringify(body), true);

        const rows: string[][] = await exportData
            .text()
            .then((rawData: string) => rawData.split("\n").map((row) => row.split(";")));

        let textContent: string[] = [];
        let tableRows: string[][] = rows;

        if (hasHeadlines) {
            textContent = rows.slice(0, 5).map((row) => row[0]); // First 5 rows treated as headlines
            tableRows = rows.slice(5); // Remaining rows for the table
        }

        const doc = new Document({
            sections: [
                {
                    properties: {
                        page: {
                            size: {
                                orientation: hasHeadlines
                                    ? PageOrientation.LANDSCAPE
                                    : PageOrientation.PORTRAIT, 
                            },
                        },
                    },
                    children: [
                        // Add headline paragraphs
                        ...textContent.map((text) => new Paragraph({ text })),

                        // Add the table
                        new Table({
                            rows: tableRows.map(
                                (row: string[]) =>
                                    new TableRow({
                                        children: row.map(
                                            (cell: string) =>
                                                new TableCell({
                                                    children: [new Paragraph(cell)],
                                                    width: { size: 20, type: WidthType.PERCENTAGE },
                                                })
                                        ),
                                    })
                            )
                        }),
                    ],
                },
            ],
        });

        const blob = await Packer.toBlob(doc);
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (error) {
        setError(errorText);
    } finally {
        setIsDownloading(false);
    }
};
